import { useEffect, useState, useCallback } from 'react'
import { apiUrl } from '../lib/api'

export interface PatternData {
  date: string
  count: number
}

interface DailyCountsResponse {
  daily_counts: PatternData[]
  generated_at: string
}

interface UsePatternsOptions {
  days?: number
}

export function usePatterns(options: UsePatternsOptions = {}) {
  const { days = 30 } = options
  const [patterns, setPatterns] = useState<PatternData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPatterns = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/api/observations/stats'))
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data: DailyCountsResponse = await response.json()
      
      // Get last N days of data
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)
      
      const filteredData = data.daily_counts
        .filter(d => new Date(d.date) >= cutoffDate)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      
      setPatterns(filteredData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch patterns')
      setPatterns([])
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    fetchPatterns()
    
    // Poll every 5 minutes
    const interval = setInterval(fetchPatterns, 300000)
    return () => clearInterval(interval)
  }, [fetchPatterns])

  return { patterns, loading, error, refetch: fetchPatterns }
}
